import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const NETWORKS = ['Google Ads', 'Facebook Ads', 'LinkedIn Ads'];
const NETWORK_COLORS = { 'Google Ads':'#4285F4', 'Facebook Ads':'#1877F2', 'LinkedIn Ads':'#0A66C2' };

const INITIAL_CAMPAIGNS = [
  { id:1, name:'Club Recruitment — UK',        network:'Google Ads',   status:'Active',  budget:500, spent:312, impressions:48200, clicks:1840, conversions:42, cpc:0.17, cpa:7.43, startDate:'Mar 1, 2026',  endDate:'Apr 30, 2026' },
  { id:2, name:'Scout Sign-up — West Africa',  network:'Facebook Ads', status:'Active',  budget:300, spent:198, impressions:32400, clicks:2100, conversions:87, cpc:0.09, cpa:2.28, startDate:'Mar 15, 2026', endDate:'Apr 30, 2026' },
  { id:3, name:'Premium Upgrade — Scouts',     network:'LinkedIn Ads', status:'Active',  budget:200, spent:143, impressions:12000, clicks:480,  conversions:18, cpc:0.30, cpa:7.94, startDate:'Apr 1, 2026',  endDate:'Apr 30, 2026' },
  { id:4, name:'Retargeting — Club Visitors',  network:'Google Ads',   status:'Paused',  budget:150, spent:67,  impressions:8400,  clicks:310,  conversions:9,  cpc:0.22, cpa:7.44, startDate:'Feb 1, 2026',  endDate:'Mar 31, 2026' },
  { id:5, name:'Brand Awareness — Global',     network:'Facebook Ads', status:'Draft',   budget:1000,spent:0,   impressions:0,     clicks:0,    conversions:0,  cpc:0,    cpa:0,    startDate:'May 1, 2026',  endDate:'May 31, 2026' },
];

const PERF_DATA = [
  { week:'Wk 1', impressions:18000, clicks:820, conversions:32 },
  { week:'Wk 2', impressions:24000, clicks:1100, conversions:48 },
  { week:'Wk 3', impressions:21000, clicks:940, conversions:41 },
  { week:'Wk 4', impressions:29000, clicks:1560, conversions:65 },
];

const statusBadge = s => ({ Active:'badge badge-green', Paused:'badge badge-amber', Draft:'badge badge-gray' })[s] || 'badge badge-gray';

export default function Ads({ toast }) {
  const [campaigns, setCampaigns] = useState(INITIAL_CAMPAIGNS);
  const [filter, setFilter]       = useState('All');
  const [creating, setCreating]   = useState(false);
  const [newCampaign, setNewCampaign] = useState({ name:'', network:'Google Ads', budget:'', startDate:'', endDate:'' });

  const filtered = filter==='All' ? campaigns : campaigns.filter(c=>c.network===filter||c.status===filter);
  const totalSpend = campaigns.reduce((s,c)=>s+c.spent,0);
  const totalConversions = campaigns.reduce((s,c)=>s+c.conversions,0);
  const totalClicks = campaigns.reduce((s,c)=>s+c.clicks,0);
  const avgCPA = totalConversions ? (totalSpend/totalConversions).toFixed(2) : 0;

  const toggle = id => { setCampaigns(prev=>prev.map(c=>c.id===id?{...c,status:c.status==='Active'?'Paused':'Active'}:c)); toast?.('Campaign status updated'); };

  const create = () => {
    if (!newCampaign.name||!newCampaign.budget) { toast?.('Fill in name and budget','warning'); return; }
    const c = { id:Date.now(), ...newCampaign, budget:Number(newCampaign.budget), spent:0, impressions:0, clicks:0, conversions:0, cpc:0, cpa:0, status:'Draft' };
    setCampaigns(prev=>[...prev,c]); setCreating(false); setNewCampaign({ name:'', network:'Google Ads', budget:'', startDate:'', endDate:'' }); toast?.('Campaign created');
  };

  const Tip = ({ active, payload, label }) => active&&payload?.length ? (
    <div style={{ background:'#131B1B', border:'1px solid #2a3a3a', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
      <p style={{ color:'var(--text-muted)', marginBottom:4 }}>{label}</p>
      {payload.map((p,i)=><p key={i} style={{ color:p.color }}>{p.name}: {p.value.toLocaleString()}</p>)}
    </div>
  ) : null;

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total spend</div><div className="stat-value">${totalSpend.toLocaleString()}</div><div className="stat-sub">All networks</div></div>
        <div className="stat-card"><div className="stat-label">Total clicks</div><div className="stat-value">{totalClicks.toLocaleString()}</div><div className="stat-sub stat-up">All campaigns</div></div>
        <div className="stat-card"><div className="stat-label">Conversions</div><div className="stat-value">{totalConversions}</div><div className="stat-sub stat-up">Form submissions</div></div>
        <div className="stat-card"><div className="stat-label">Avg CPA</div><div className="stat-value">${avgCPA}</div><div className="stat-sub">Cost per conversion</div></div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header"><span className="card-title">Performance over time</span></div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={PERF_DATA} margin={{ top:4,right:4,left:-20,bottom:0 }}>
            <XAxis dataKey="week" tick={{ fill:'#646B6B',fontSize:11 }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fill:'#646B6B',fontSize:11 }} axisLine={false} tickLine={false}/>
            <Tooltip content={<Tip />}/>
            <Bar dataKey="impressions" name="Impressions" fill="#4fc3f7" radius={[4,4,0,0]}/>
            <Bar dataKey="clicks"      name="Clicks"      fill="#A7F432" radius={[4,4,0,0]}/>
            <Bar dataKey="conversions" name="Conversions" fill="#f5a623" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div className="filter-pills" style={{ marginBottom:0 }}>
          {['All','Active','Paused','Draft',...NETWORKS].map(f=><button key={f} className={`pill ${filter===f?'active':''}`} onClick={()=>setFilter(f)} style={{ fontSize:11 }}>{f}</button>)}
        </div>
        <button className="btn btn-primary" onClick={()=>setCreating(true)}>+ New campaign</button>
      </div>

      {creating && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">Create ad campaign</span><button className="btn btn-ghost" style={{ fontSize:12 }} onClick={()=>setCreating(false)}>✕</button></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Campaign name *</label><input className="search-input" value={newCampaign.name} onChange={e=>setNewCampaign(p=>({...p,name:e.target.value}))} placeholder="e.g. Scout Recruitment Q2" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Network</label><select className="search-input" value={newCampaign.network} onChange={e=>setNewCampaign(p=>({...p,network:e.target.value}))}>{NETWORKS.map(n=><option key={n}>{n}</option>)}</select></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Budget ($) *</label><input type="number" className="search-input" value={newCampaign.budget} onChange={e=>setNewCampaign(p=>({...p,budget:e.target.value}))} placeholder="500" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Start date</label><input type="date" className="search-input" value={newCampaign.startDate} onChange={e=>setNewCampaign(p=>({...p,startDate:e.target.value}))} /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>End date</label><input type="date" className="search-input" value={newCampaign.endDate} onChange={e=>setNewCampaign(p=>({...p,endDate:e.target.value}))} /></div>
          </div>
          <div style={{ display:'flex',gap:8 }}><button className="btn btn-primary" onClick={create}>Create campaign</button><button className="btn btn-ghost" onClick={()=>setCreating(false)}>Cancel</button></div>
        </div>
      )}

      <div className="card" style={{ padding:0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Campaign</th><th>Network</th><th>Budget</th><th>Spent</th><th>Impressions</th><th>Clicks</th><th>Conversions</th><th>CPA</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {filtered.map(c=>(
                <tr key={c.id}>
                  <td style={{ fontWeight:500 }}>{c.name}</td>
                  <td><span style={{ padding:'3px 8px',borderRadius:99,fontSize:11,fontWeight:600,background:`${NETWORK_COLORS[c.network]}22`,color:NETWORK_COLORS[c.network] }}>{c.network}</span></td>
                  <td>${c.budget}</td>
                  <td style={{ color:'var(--accent)',fontWeight:600 }}>${c.spent}</td>
                  <td>{c.impressions.toLocaleString()}</td>
                  <td>{c.clicks.toLocaleString()}</td>
                  <td style={{ color:'#4fc3f7',fontWeight:600 }}>{c.conversions}</td>
                  <td>${c.cpa}</td>
                  <td><span className={statusBadge(c.status)}>{c.status}</span></td>
                  <td><button className="btn btn-ghost" style={{ fontSize:11,padding:'4px 10px' }} onClick={()=>toggle(c.id)}>{c.status==='Active'?'Pause':'Activate'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
